import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { EchoflicksVideoSession } from "../types";

interface CreativeLineageCardProps {
  lineage: EchoflicksVideoSession["creative_lineage"];
}

function displayValue(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
}

export function CreativeLineageCard({ lineage }: CreativeLineageCardProps) {
  if (!lineage) return null;

  const artifacts = [...(lineage.music_artifacts ?? [])].sort(
    (left, right) => left.order - right.order,
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Creative intent audit</CardTitle>
          <Badge variant="outline">Composer fenced</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          The approved intent and ordered music transformations used for this
          Echo. Digests prove which values reached each stage.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Intent digest</dt>
            <dd className="break-all font-mono text-xs">
              {lineage.intent_digest ?? "Unavailable"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Checkpoint</dt>
            <dd className="break-all font-mono text-xs">
              {lineage.checkpoint_namespace ?? "Unavailable"}@
              {lineage.checkpoint_version ?? "?"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Script digest</dt>
            <dd className="break-all font-mono text-xs">
              {lineage.script_artifact_digest ?? "Unavailable"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Music lineage digest</dt>
            <dd className="break-all font-mono text-xs">
              {lineage.music_lineage_digest ?? "Unavailable"}
            </dd>
          </div>
        </dl>

        {artifacts.length > 0 ? (
          <ol className="space-y-3" aria-label="Ordered music lineage">
            {artifacts.map((artifact) => (
              <li
                key={`${artifact.type}:${artifact.artifact_digest}`}
                className="rounded-md border p-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {artifact.order}. {artifact.type.replaceAll("_", " ")}
                  </span>
                  <code className="break-all text-xs text-muted-foreground">
                    {artifact.artifact_digest}
                  </code>
                </div>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
                  {displayValue(artifact.value)}
                </pre>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">
            No music transformation artifacts were recorded.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
